import React, { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";
import { useTable } from "react-table";
import {
  FaBoxOpen,
  FaTags,
  FaMoneyBillWave,
  FaUsers,
  FaShoppingCart,
  FaCalendarDay,
  FaClipboardList,
  FaChartBar,
  FaChartPie,
} from "react-icons/fa";
import displayINRCurrency from "../helpers/displayCurrency";
import SummaryApi from "../common";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const AdminDashboard = () => {

  /* ───────── DASHBOARD COUNTS API ───────── */
  const [dashboardCounts, setDashboardCounts] = useState(null);
  const [countsLoading, setCountsLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardCounts = async () => {
      try {
        const response = await fetch(
          SummaryApi.getalldashboardcounts.url,
          {
            method: SummaryApi.getalldashboardcounts.method,
            headers: { "Content-Type": "application/json" },
          }
        );

        const result = await response.json();
        if (result.status === true) {
          setDashboardCounts(result.data);
        }
      } catch (error) {
        console.error("Dashboard counts error:", error);
      } finally {
        setCountsLoading(false);
      }
    };

    fetchDashboardCounts();
  }, []);

  /* ───────── TODAY ORDERS API (UNCHANGED) ───────── */
  const [todayOrders, setTodayOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTodayOrders = async () => {
      try {
        setLoadingOrders(true);
        const response = await fetch(
          SummaryApi.today_all_orders.url,
          {
            method: SummaryApi.today_all_orders.method,
            headers: { "Content-Type": "application/json" },
          }
        );

        const result = await response.json();
        if (result.success || result.status === true) {
          setTodayOrders(result.data || []);
        } else {
          setError("Failed to load today's orders");
        }
      } catch (err) {
        setError("Network error");
      } finally {
        setLoadingOrders(false);
      }
    };

    fetchTodayOrders();
  }, []);

  const todayOrdersCount =
    dashboardCounts?.today_orders ?? todayOrders.length;

  const todayRevenue = todayOrders.reduce(
    (sum, order) => sum + Number(order.total_amount || 0),
    0
  );

  const totalRevenue = 12456780;

  /* ───────── BAR CHART DATA (UNCHANGED) ───────── */
  const productOrderCounts = [
    { product_name: "Rose Glow Serum", order_count: 142 },
    { product_name: "Hydrating Face Mist", order_count: 118 },
    { product_name: "Vitamin C Cream", order_count: 95 },
    { product_name: "Lip Tint - Coral Kiss", order_count: 87 },
    { product_name: "Silk Hair Oil", order_count: 72 },
    { product_name: "Matte Lipstick Set", order_count: 65 },
  ];

  const barData = {
    labels: productOrderCounts.map(p => p.product_name),
    datasets: [
      {
        label: "Number of Orders",
        data: productOrderCounts.map(p => p.order_count),
        backgroundColor: "rgba(255, 182, 193, 0.4)",
        borderColor: "rgba(255, 105, 180, 1)",
        borderWidth: 2,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
  };

  /* ───────── PIE CHART DATA (UNCHANGED) ───────── */
  const pieData = {
    labels: [
      "Delivered",
      "Processing",
      "Order in transit",
      "Cancelled",
      "Order placed",
    ],
    datasets: [
      {
        data: [18, 3, 2, 2, 1],
        backgroundColor: [
          "#FFB6C1",
          "#FFC0CB",
          "#FFD1DC",
          "#FF69B4",
          "#FF1493",
        ],
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
  };

  /* ───────── TABLE COLUMNS (ORIGINAL – UNTOUCHED) ───────── */
  const columns = React.useMemo(
    () => [
      { Header: "Order Code", accessor: "order_code", Cell: ({ value }) => value || "N/A" },
      { Header: "Customer Name", accessor: "user_name", Cell: ({ value }) => value || "N/A" },
      { Header: "Mobile", accessor: "mobile_number", Cell: ({ value }) => value || "N/A" },
      {
        Header: "Order Amount",
        accessor: "order_amount",
        Cell: ({ value }) => value ? displayINRCurrency(value) : "N/A",
      },
      {
        Header: "Delivery Charges",
        accessor: "delivery_charges",
        Cell: ({ value }) => value ? displayINRCurrency(value) : "N/A",
      },
      {
        Header: "Total Amount",
        accessor: "total_amount",
        Cell: ({ value }) => value ? displayINRCurrency(value) : "N/A",
      },
      {
        Header: "Order Date",
        accessor: "order_date",
        Cell: ({ value }) =>
          value ? new Date(value).toLocaleDateString("en-IN") : "N/A",
      },
      {
        Header: "Order Status",
        accessor: "order_status",
        Cell: ({ value }) => (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-pink-100 text-pink-800">
            {value || "N/A"}
          </span>
        ),
      },
      { Header: "Address", accessor: "order_address", Cell: ({ value }) => value || "N/A" },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({ columns, data: todayOrders });

  return (
    <>
      <h1 className="text-2xl md:text-4xl font-extrabold mb-10 text-pink-800 text-center tracking-wide px-4">
        Dashboard
      </h1>

           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10 px-4">


        <div className="bg-white rounded-2xl shadow-md border border-pink-100 p-6 flex items-center gap-4 hover:shadow-xl transition">
          <FaBoxOpen className="text-pink-600 text-4xl" />
          <div>
            <p>Products</p>
            <p className="text-3xl font-bold">
              {countsLoading ? "—" : dashboardCounts?.total_products}
            </p>
          </div>
        </div>

       <div className="bg-white rounded-2xl shadow-md border border-pink-100 p-6 flex items-center gap-4 hover:shadow-xl transition">
          <FaTags className="text-pink-600 text-4xl" />
          <div>
            <p>Packages</p>
            <p className="text-3xl font-bold">
              {countsLoading ? "—" : dashboardCounts?.total_packages}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-md border border-pink-100 p-6 flex items-center gap-4 hover:shadow-xl transition">
          <FaUsers className="text-pink-600 text-4xl" />
          <div>
            <p>Customers</p>
            <p className="text-3xl font-bold">
              {countsLoading ? "—" : dashboardCounts?.total_users}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-md border border-pink-100 p-6 flex items-center gap-4 hover:shadow-xl transition">
          <FaShoppingCart className="text-pink-600 text-4xl" />
          <div>
            <p>Total Orders</p>
            <p className="text-3xl font-bold">
              {countsLoading ? "—" : dashboardCounts?.total_orders}
            </p>
          </div>
        </div>

        <div className="lg:col-span-2 bg-pink-100 rounded-2xl p-6 flex items-center gap-4">
          <FaMoneyBillWave className="text-pink-700 text-5xl" />
          <div>
            <p>Total Revenue</p>
            <p className="text-3xl font-bold">
              {displayINRCurrency(totalRevenue)}
            </p>
          </div>
        </div>

        <div className="bg-pink-100 rounded-2xl p-6">
          <FaCalendarDay className="text-pink-700 text-5xl" />
          <p className="text-3xl font-bold">{todayOrdersCount}</p>
          <p>Revenue: {displayINRCurrency(todayRevenue)}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-md border border-pink-100 p-6 hover:shadow-xl transition">
          <FaClipboardList className="text-pink-600 text-3xl" />
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="bg-pink-50 rounded-xl py-3 text-center">
              <p>Total Plans</p>
              <p className="text-2xl font-bold">
                {dashboardCounts?.total_plans}
              </p>
            </div>
            <div className="bg-pink-50 rounded-xl py-3 text-center">
              <p>Active</p>
              <p className="text-2xl font-bold">
                {dashboardCounts?.total_active_plans}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Section */}
      <div className="space-y-10 px-4 mb-12">
        {/* Top Product Sales – Insight Driven */}
        <div className="relative bg-gradient-to-br from-pink-50 to-white rounded-3xl border border-pink-200 shadow-xl p-8 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-pink-200/40 rounded-full blur-3xl" />

          <div className="relative grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <FaChartBar className="text-pink-600 text-3xl" />
                  <h2 className="text-2xl font-bold text-pink-900">
                    Top Product Sales
                  </h2>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Visual breakdown of best-performing products based on total sales
                  volume. Identify demand trends and inventory focus.
                </p>
              </div>

              <div className="mt-6 bg-white rounded-2xl border border-pink-100 p-5 shadow-sm">
                <p className="text-xs text-gray-500">Top Product Revenue</p>
                <p className="text-2xl font-extrabold text-pink-800 mt-1">
                  {displayINRCurrency(totalRevenue)}
                </p>
                <p className="text-xs text-green-600 mt-1">▲ Strong performance</p>
              </div>
            </div>

            <div className="lg:col-span-2 bg-white/70 backdrop-blur-md rounded-2xl p-6 border border-pink-100 shadow-inner">
              <div className="h-[340px]">
                <Bar data={barData} options={barOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* Order Status – Donut Focus */}
        <div className="relative bg-white rounded-3xl border border-pink-100 shadow-xl p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-[260px] h-[260px] bg-pink-50 rounded-full p-4 shadow-inner">
                <Pie data={pieData} options={pieOptions} />
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <FaChartPie className="text-pink-600 text-3xl" />
                <h2 className="text-2xl font-bold text-pink-900">
                  Order Status Overview
                </h2>
              </div>

              <p className="text-gray-600 text-sm mb-6">
                Real-time distribution of order lifecycle stages, helping you track
                fulfillment efficiency and bottlenecks.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500">Completed</p>
                  <p className="text-xl font-bold text-green-700">✔</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500">Processing</p>
                  <p className="text-xl font-bold text-yellow-700">⏳</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500">Shipped</p>
                  <p className="text-xl font-bold text-blue-700">🚚</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500">Cancelled</p>
                  <p className="text-xl font-bold text-red-700">✖</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Today's Orders Table */}
      <div className="bg-white/90 backdrop-blur rounded-2xl shadow-xl mb-8 border border-pink-100 mx-4">
        <div className="p-6">
          <h2 className="text-xl md:text-2xl font-bold mb-6 text-pink-800 text-center">
            Today's Orders
          </h2>

<div className="overflow-x-auto overflow-y-auto max-h-[420px] rounded-lg border border-pink-100">
  <table {...getTableProps()} className="w-full text-sm border-collapse">

    {/* STATIC HEADERS – ALWAYS VISIBLE */}
    <thead className="bg-pink-100">
      {headerGroups.map((headerGroup) => (
        <tr {...headerGroup.getHeaderGroupProps()}>
          {headerGroup.headers.map((column) => (
            <th
              {...column.getHeaderProps()}
              className="p-4 text-left font-semibold text-pink-900 
                         bg-pink-100 sticky top-0 z-20"
            >
              {column.render("Header")}
            </th>
          ))}
        </tr>
      ))}
    </thead>

    {/* DYNAMIC BODY */}
    <tbody {...getTableBodyProps()} className="bg-white">

      {/* LOADING STATE */}
      {loadingOrders && (
        <tr>
          <td
            colSpan={columns.length}
            className="p-6 text-center text-gray-500"
          >
            Loading today's orders...
          </td>
        </tr>
      )}

      {/* ERROR STATE */}
      {!loadingOrders && error && (
        <tr>
          <td
            colSpan={columns.length}
            className="p-6 text-center text-red-600 font-medium"
          >
            {error}
          </td>
        </tr>
      )}

      {/* NO DATA STATE */}
      {!loadingOrders && !error && todayOrders.length === 0 && (
        <tr>
          <td
            colSpan={columns.length}
            className="p-6 text-center text-gray-600"
          >
            No order details found for today.
          </td>
        </tr>
      )}

      {/* DATA ROWS */}
      {!loadingOrders && !error && rows.map((row) => {
        prepareRow(row);
        return (
          <tr
            {...row.getRowProps()}
            className="border-b border-pink-50 hover:bg-pink-50 transition"
          >
            {row.cells.map((cell) => (
              <td
                {...cell.getCellProps()}
                className="p-4 text-gray-800"
              >
                {cell.render("Cell")}
              </td>
            ))}
          </tr>
        );
      })}
    </tbody>

  </table>
</div>


      
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
